import { useCallback, useMemo, useState } from "react";
import { AppEventPublisher } from "src/AppEventPublisher";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "src/sudokit/components/Dialog";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { PersistentChatWithMessages } from "~shared/types/persistent-chat-types";
import {
  ChatHistoryListChrome,
  defaultChatIcons,
  extractSnippet,
  type ChatHistoryListItem,
} from "@sudowrite/shared-chat";

import { copyChatConversation } from "./chatConversationCopyUtils";
import { sanitizeChatTitleForDisplay } from "./chatTitleUtils";
import SeriesTag from "../StoryBible/SeriesTag";

type ChatListViewProps = {
  chats: PersistentChatWithMessages[];
  /** Firebase project id for the book the user is currently viewing. */
  currentProjectId: string;
  /** True while the project chat list is loading and there is no cached data yet. */
  isLoading?: boolean;
  /** True when the last request for the project chat list failed (and there is no cached data to show). */
  isError?: boolean;
  onRetryChats?: () => void;
  searchQuery: string;
  currentChatId: string | null;
  onSelectChat: (chatId: string, chatProjectId?: string) => void;
  onRenameChat: (chatId: string, newTitle: string) => void;
  onDeleteChat: (chatId: string) => void;
  hasMoreChats?: boolean;
  isLoadingMoreChats?: boolean;
  onLoadMoreChats?: () => boolean;
  onStartNewChat?: () => void;
};

const chatHistoryIcons = {
  copy: defaultChatIcons.copy,
  pencil: defaultChatIcons.pencil,
  delete: defaultChatIcons.delete,
  more: defaultChatIcons.more,
};

function mapChatsToHistoryItems(
  chats: PersistentChatWithMessages[],
  searchQuery: string,
  currentProjectId: string,
): ChatHistoryListItem[] {
  const trimmedQuery = searchQuery.trim();
  const query = trimmedQuery.toLowerCase();

  return chats
    .map((chat) => {
      const snippets = query
        ? (chat.messages || [])
            .filter((msg) => (msg.content ?? "").toLowerCase().includes(query))
            .map((msg) => extractSnippet(msg.content ?? "", query))
        : [];

      return {
        chat,
        snippets,
        title: sanitizeChatTitleForDisplay(chat.title),
      };
    })
    .filter(({ chat, snippets, title }) => {
      if (!trimmedQuery) return true;
      const titleMatches = title.toLowerCase().includes(query);
      return titleMatches || snippets.length > 0;
    })
    .map(({ chat, snippets, title }) => ({
      id: chat.id,
      projectId: chat.project_id,
      title,
      updatedAt: new Date(chat.updated_at),
      snippets,
      seriesBookLabel:
        chat.is_current_project === false
        || (chat.project_id !== currentProjectId && !!chat.source_project_title)
          ? chat.source_project_title || null
          : null,
    }));
}

export const ChatListView: React.FC<ChatListViewProps> = ({
  chats,
  currentProjectId,
  isLoading = false,
  isError = false,
  onRetryChats,
  searchQuery,
  currentChatId,
  onSelectChat,
  onRenameChat,
  onDeleteChat,
  hasMoreChats = false,
  isLoadingMoreChats = false,
  onLoadMoreChats,
  onStartNewChat,
}) => {
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const [copyingChatId, setCopyingChatId] = useState<string | null>(null);

  const safeChats = useMemo(() => (Array.isArray(chats) ? chats : []), [chats]);
  const items = useMemo(
    () => mapChatsToHistoryItems(safeChats, searchQuery, currentProjectId),
    [safeChats, searchQuery, currentProjectId],
  );

  const handleSelectItem = useCallback(
    (item: ChatHistoryListItem) => {
      const chat = safeChats.find((entry) => entry.id === item.id);
      AppEventPublisher.agenticChat.selectChat(item.id, chat?.messages?.length || 0);
      onSelectChat(item.id, item.projectId);
    },
    [onSelectChat, safeChats],
  );

  const handleRenameItem = useCallback(
    (chatId: string, newTitle: string) => {
      AppEventPublisher.agenticChat.renameChat(chatId);
      onRenameChat(chatId, newTitle);
    },
    [onRenameChat],
  );

  const handleCopyItem = useCallback(
    async (item: ChatHistoryListItem) => {
      if (copyingChatId === item.id) return;

      const chat = safeChats.find((entry) => entry.id === item.id);
      setCopyingChatId(item.id);
      try {
        const result = await copyChatConversation(item.id, chat?.title ?? null);
        if (result.copied) {
          AppEventPublisher.agenticChat.copyChat(item.id, result.messageCount);
        }
      } catch {
        // Clipboard writes and chat fetches can fail in insecure/offline contexts; ignore silently.
      } finally {
        setCopyingChatId(null);
      }
    },
    [copyingChatId, safeChats],
  );

  const handleConfirmDelete = () => {
    if (!deletingChatId) return;
    const deletingChat = safeChats.find((chat) => chat.id === deletingChatId);
    AppEventPublisher.agenticChat.deleteChat(deletingChatId, deletingChat?.messages?.length || 0);
    onDeleteChat(deletingChatId);
    setDeletingChatId(null);
  };

  return (
    <>
      <WithReset className="flex flex-1 min-h-0 flex-col">
        <ChatHistoryListChrome
          items={items}
          searchQuery={searchQuery}
          isLoading={isLoading}
          isError={isError}
          onRetry={onRetryChats}
          currentItemId={currentChatId}
          onSelectItem={handleSelectItem}
          onRenameItem={handleRenameItem}
          onCopyItem={(item) => void handleCopyItem(item)}
          onDeleteItemRequest={setDeletingChatId}
          hasMoreItems={hasMoreChats}
          isLoadingMoreItems={isLoadingMoreChats}
          onLoadMoreItems={onLoadMoreChats}
          icons={chatHistoryIcons}
          className="flex flex-1 min-h-0 flex-col"
          listClassName="-mr-2 pr-2 [scrollbar-width:thin] [scrollbar-color:var(--scrollbar-thumb)_var(--scrollbar-track)] animate-in fade-in py-0.5 outline-none"
          onStartNewChat={onStartNewChat}
          renderSeriesLabel={(projectTitle) => (
            <SeriesTag
              projectTitle={projectTitle}
              variant="chiclet"
              className="self-start"
            />
          )}
        />
      </WithReset>

      <Dialog
        open={!!deletingChatId}
        onOpenChange={(open) => !open && setDeletingChatId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>This will move your chat to the project trash.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <SudoButton
              variant="tertiaryGrey"
              onClick={() => setDeletingChatId(null)}
            >
              Cancel
            </SudoButton>
            <SudoButton
              variant="danger"
              onClick={handleConfirmDelete}
            >
              Yes, I'm sure
            </SudoButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
